import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { fetchPendingRefunds } from '@/features/operations/api';
import { formatHistoryTimestamp } from '@/features/operations/labels';
import { formatPrice } from '@/features/operations/money';
import type { PendingRefund } from '@/features/operations/types';

function deviceName(device: PendingRefund['device']) {
  return [device.brand, device.model, device.storageGb ? `${device.storageGb} GB` : null]
    .filter(Boolean)
    .join(' ');
}

/**
 * Approved online payments that no longer support a sale and wait for their manual refund
 * (VEN-6, VEN-8), on the pages the team opens every day. Shows nothing when there are none.
 */
export function PendingRefundsAlert({ token }: { token: string }) {
  const [refunds, setRefunds] = useState<PendingRefund[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    fetchPendingRefunds(token)
      .then((items) => active && setRefunds(items))
      .catch(() => active && setLoadError(true));
    return () => {
      active = false;
    };
  }, [token]);

  if (loadError) {
    return (
      <Alert severity="warning">
        No pudimos verificar si hay pagos online para devolver. Recargá la página para volver a
        intentarlo.
      </Alert>
    );
  }

  if (!refunds?.length) {
    return null;
  }

  return (
    <Alert severity="warning">
      <AlertTitle>
        {refunds.length === 1
          ? 'Hay 1 pago online pendiente de devolución'
          : `Hay ${refunds.length} pagos online pendientes de devolución`}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Son pagos aprobados tarde o asociados a ventas revertidas. Devolvé el total desde Mercado
        Pago y registralo en la ficha del equipo.
      </Typography>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
        {refunds.map((refund) => (
          <li key={refund.id}>
            <Link component={RouterLink} to={`/admin/devices/${refund.device.id}`}>
              {deviceName(refund.device)}
            </Link>{' '}
            · {refund.buyerName} · {formatPrice(refund.amountMinor)}
            {refund.refundRequirement === 'REVERSED_SALE' ? ' · venta revertida' : ''}
            {refund.approvedAt ? ` · aprobado el ${formatHistoryTimestamp(refund.approvedAt)}` : ''}
          </li>
        ))}
      </Stack>
    </Alert>
  );
}
